import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import { CheckCircle, Trash2, MoreHorizontal, ArrowUpRight } from "lucide-react";
import Breadcrumb, { BreadcrumbPill } from "./Breadcrumb";
import ConfirmModal from "./ConfirmModal";
import { User, loadUsers, displayName, initials, avatarColor } from "./user-display";
import { formatRelative } from "./category-rows";

interface Category {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
}

interface Question {
    id: string;
    authorId: string;
    title: string;
    body: string | null;
    categoryId: string | null;
    promotedPageId: string | null;
    createdAt: string;
    updatedAt: string;
}

interface Comment {
    id: string;
    authorId: string;
    body: string;
    parentCommentId: string | null;
    isAnswered: boolean;
    answeredBy: string | null;
    answeredAt: string | null;
    createdAt: string;
}

interface Props {
    questionId: string;
    categories: Category[];
    currentUserId: string | null;
    isAdmin: boolean;
    canManage: boolean;
    onBack: () => void;
    onNavigatePage: (pageId: string, categoryId: string) => void;
    onDeleted: () => void;
}

export default function QuestionDetailView({
    questionId,
    categories,
    currentUserId,
    isAdmin,
    canManage,
    onBack,
    onNavigatePage,
    onDeleted,
}: Props) {
    const [loading, setLoading] = useState(true);
    const [question, setQuestion] = useState<Question | null>(null);
    const [comments, setComments] = useState<Comment[]>([]);
    const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());
    const [newBody, setNewBody] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [deleteQuestionOpen, setDeleteQuestionOpen] = useState(false);
    const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    const categoriesById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            axios.get(`/api/manuals/questions/${questionId}`),
            axios.get(`/api/manuals/questions/${questionId}/comments`),
            loadUsers(),
        ])
            .then(([qRes, cRes, users]) => {
                setUsersMap(new Map(users.map(u => [u.id, u])));
                if (qRes.data.success) setQuestion(qRes.data.item);
                else toast.error(qRes.data.message || "Failed to load question");
                if (cRes.data.success) setComments(cRes.data.items);
            })
            .catch(() => toast.error("Failed to load question"))
            .finally(() => setLoading(false));
    }, [questionId]);

    useEffect(() => {
        if (!menuOpen) return;
        const h = () => setMenuOpen(false);
        document.addEventListener("click", h);
        return () => document.removeEventListener("click", h);
    }, [menuOpen]);

    function reload() {
        axios.get(`/api/manuals/questions/${questionId}/comments`)
            .then(r => { if (r.data.success) setComments(r.data.items); })
            .catch(() => {});
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!newBody.trim()) return;
        setSubmitting(true);
        axios.post(`/api/manuals/questions/${questionId}/comments`, {
            targetId: questionId,
            targetType: "question",
            parentCommentId: null,
            body: newBody.trim(),
        })
            .then(res => {
                if (res.data.success) {
                    setNewBody("");
                    reload();
                } else {
                    toast.error(res.data.message || "Failed to reply");
                }
            })
            .catch(() => toast.error("Failed to reply"))
            .finally(() => setSubmitting(false));
    }

    function handleMarkAnswered(commentId: string) {
        axios.post(`/api/manuals/comments/${commentId}/mark-answered`)
            .then(res => {
                if (res.data.success) {
                    toast.success("Marked as the answer");
                    reload();
                } else {
                    toast.error(res.data.message || "Failed to mark as answer");
                }
            })
            .catch(err => toast.error(err.response?.data?.message || "Failed to mark as answer"));
    }

    function handleDeleteQuestion() {
        setDeleteQuestionOpen(false);
        axios.delete(`/api/manuals/questions/${questionId}`)
            .then(res => {
                if (res.data.success) {
                    toast.success("Question deleted");
                    onDeleted();
                } else {
                    toast.error(res.data.message || "Failed to delete");
                }
            })
            .catch(() => toast.error("Failed to delete"));
    }

    function handleDeleteComment() {
        if (!deleteCommentId) return;
        const id = deleteCommentId;
        setDeleteCommentId(null);
        axios.delete(`/api/manuals/comments/${id}`)
            .then(res => {
                if (res.data.success) reload();
                else toast.error(res.data.message || "Failed to delete");
            })
            .catch(() => toast.error("Failed to delete"));
    }

    function handlePromote() {
        setMenuOpen(false);
        axios.post(`/api/manuals/questions/${questionId}/promote`)
            .then(res => {
                if (res.data.success) {
                    toast.success("Promoted to draft page");
                    const cat = question?.categoryId;
                    if (cat) onNavigatePage(res.data.item.id, cat);
                } else {
                    toast.error(res.data.message || "Failed to promote");
                }
            })
            .catch(err => toast.error(err.response?.data?.message || "Failed to promote"));
    }

    function authorView(authorId: string) {
        const u = usersMap.get(authorId);
        const name = u ? displayName(u) : authorId.slice(0, 8);
        return { name, color: avatarColor(authorId), initial: initials(name) };
    }

    if (loading || !question) {
        return (
            <div className="m-cat-page">
                <div className="m-loading"><CustomLoading /></div>
            </div>
        );
    }

    const cat = question.categoryId ? categoriesById.get(question.categoryId) : null;
    const pills: BreadcrumbPill[] = [
        { kind: "page", label: "Questions & Answers", onClick: onBack },
        { kind: "page", label: question.title },
    ];
    const a = authorView(question.authorId);
    const isAsker = question.authorId === currentUserId;
    const canMarkAnswer = isAdmin || isAsker;
    // Promoting creates a draft page — a content-creation action, so read-only users
    // can't promote even their own questions; requires editor/admin (canManage).
    const canPromote = canManage && (isAdmin || isAsker) && !question.promotedPageId && question.categoryId;
    const canDeleteQuestion = isAdmin || isAsker;

    const topLevel = comments.filter(c => !c.parentCommentId);
    const repliesOf = (id: string) => comments.filter(c => c.parentCommentId === id);

    return (
        <div className="m-cat-page">
            <Breadcrumb pills={pills} />

            <div className="m-question-detail">
                <div className="m-question-detail-head">
                    <span className="m-comment-avatar sm" style={{ background: a.color }}>{a.initial}</span>
                    <span className="m-question-meta">
                        <strong>{a.name}</strong> asked {formatRelative(question.createdAt)}
                        {cat && <> in <span style={{ color: cat.color || undefined }}>{cat.name}</span></>}
                    </span>
                    {(canDeleteQuestion || canPromote) && (
                        <div className="m-question-kebab-wrap">
                            <button
                                className="m-cat-strip-kebab"
                                onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                            >
                                <MoreHorizontal size={14} />
                            </button>
                            {menuOpen && (
                                <div className="m-cat-kebab-menu">
                                    {canPromote && (
                                        <button onClick={handlePromote}>
                                            <ArrowUpRight size={13} /> Promote to page
                                        </button>
                                    )}
                                    {canDeleteQuestion && (
                                        <button className="m-cat-kebab-danger" onClick={() => { setMenuOpen(false); setDeleteQuestionOpen(true); }}>
                                            <Trash2 size={13} /> Delete
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <h1 className="m-question-detail-title">{question.title}</h1>
                {question.body && <div className="m-question-detail-body">{question.body}</div>}
                {question.promotedPageId && question.categoryId && (
                    <button
                        className="m-btn m-btn-ghost m-btn-sm"
                        onClick={() => onNavigatePage(question.promotedPageId!, question.categoryId!)}
                    >
                        Open promoted page
                    </button>
                )}
            </div>

            <div className="m-question-replies">
                <div className="m-question-replies-header">
                    {topLevel.length} {topLevel.length === 1 ? "reply" : "replies"}
                </div>

                {topLevel.map(reply => {
                    const ra = authorView(reply.authorId);
                    const canDelReply = isAdmin || reply.authorId === currentUserId;
                    return (
                        <div key={reply.id} className={`m-question-reply${reply.isAnswered ? " answered" : ""}`}>
                            <div className="m-question-reply-main">
                                <span className="m-comment-avatar" style={{ background: ra.color }}>{ra.initial}</span>
                                <div className="m-question-reply-text">
                                    <div className="m-comment-meta">
                                        <span className="m-comment-author">{ra.name}</span>
                                        <span className="m-comment-time">{formatRelative(reply.createdAt)}</span>
                                        {reply.isAnswered && (
                                            <span className="m-question-answer-badge">
                                                <CheckCircle size={13} /> Answer
                                            </span>
                                        )}
                                    </div>
                                    <div className="m-comment-text">{reply.body}</div>
                                    <div className="m-comment-actions">
                                        {canMarkAnswer && !reply.isAnswered && (
                                            <button onClick={() => handleMarkAnswered(reply.id)}>
                                                <CheckCircle size={13} /> Mark as answer
                                            </button>
                                        )}
                                        {canDelReply && (
                                            <button className="danger" onClick={() => setDeleteCommentId(reply.id)}>
                                                <Trash2 size={13} /> Delete
                                            </button>
                                        )}
                                    </div>

                                    {repliesOf(reply.id).map(sub => {
                                        const sa = authorView(sub.authorId);
                                        const canDelSub = isAdmin || sub.authorId === currentUserId;
                                        return (
                                            <div key={sub.id} className="m-comment m-comment-reply">
                                                <span className="m-comment-avatar sm" style={{ background: sa.color }}>{sa.initial}</span>
                                                <div className="m-comment-body">
                                                    <div className="m-comment-meta">
                                                        <span className="m-comment-author">{sa.name}</span>
                                                        <span className="m-comment-time">{formatRelative(sub.createdAt)}</span>
                                                    </div>
                                                    <div className="m-comment-text">{sub.body}</div>
                                                    {canDelSub && (
                                                        <div className="m-comment-actions">
                                                            <button className="danger" onClick={() => setDeleteCommentId(sub.id)}>
                                                                <Trash2 size={13} /> Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}

                <form className="m-comment-form" onSubmit={handleSubmit}>
                    <textarea
                        className="m-input"
                        rows={3}
                        value={newBody}
                        onChange={e => setNewBody(e.target.value)}
                        placeholder="Write a reply..."
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                        <button
                            type="submit"
                            className="m-btn m-btn-primary m-btn-sm"
                            disabled={submitting || !newBody.trim()}
                        >
                            Reply
                        </button>
                    </div>
                </form>
            </div>

            {deleteQuestionOpen && (
                <ConfirmModal
                    title="Delete question"
                    message={`Delete "${question.title}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={handleDeleteQuestion}
                    onCancel={() => setDeleteQuestionOpen(false)}
                />
            )}

            {deleteCommentId && (
                <ConfirmModal
                    title="Delete reply"
                    message="Delete this reply? This cannot be undone."
                    confirmLabel="Delete"
                    danger
                    onConfirm={handleDeleteComment}
                    onCancel={() => setDeleteCommentId(null)}
                />
            )}
        </div>
    );
}
